import { CountrySingleResponse, ICountry, ICountryContext, isFunction, isPromiseLike } from '@ngt/opms';
import { useMemo } from 'react';
import { KeyedMutator, MutatorCallback } from 'swr';
import { GetConsentEventDataResponse, Country } from '../../api/dtos';
import useConsentEventData from '../data/useConsentEventData';



const useGenerateEConsentCountryContext = (consentEventContext: ReturnType<typeof useConsentEventData>) => {
    
    const countryContext: ICountryContext = useMemo(() => {
        const country = consentEventContext.data?.country as ICountry;
        const error = consentEventContext.error;

        const countryMutate: KeyedMutator<CountrySingleResponse> = async (data, opts) => {
            let newData: GetConsentEventDataResponse = { ...(consentEventContext.data as any), responseStatus: error as any };
            let newSingleData: CountrySingleResponse | undefined = undefined;
            
            if (!data) {
                newSingleData = undefined;
            }
            else if (isFunction(data)) {
                const existingSingleData = { country, responseStatus: error } as CountrySingleResponse;

                const callback = (data as MutatorCallback<CountrySingleResponse>)(existingSingleData);
                
                if (isPromiseLike(callback)) {
                    newSingleData = await (callback as Promise<CountrySingleResponse>);
                }
                else {
                    newSingleData = callback;
                }
            }
            else if (isPromiseLike(data)) {
                newSingleData = await (data as Promise<CountrySingleResponse>);
            }

            if (!newSingleData) {
                newData.country = undefined as any;
            }
            else {
                if (newSingleData.responseStatus) {
                    newData.responseStatus = newSingleData.responseStatus;
                }

                newData.country = newSingleData.country as Country;
            }

            const updatedData = await consentEventContext.mutate(newData, opts as any);

            if (updatedData) {
                return {
                    country: updatedData.country,
                    responseStatus: updatedData.responseStatus
                } as CountrySingleResponse
            }

            return undefined;
        }

        const countryRefresh = async () => {
            const newData = await consentEventContext.refresh();

            return newData?.country;
        }

        return {
            data: country,
            error: consentEventContext.error,
            loading: consentEventContext.loading,
            validating: consentEventContext.validating,
            mutate: countryMutate,
            refresh: countryRefresh
        }
    }, [consentEventContext])

    return countryContext
}

export default useGenerateEConsentCountryContext;